// One server basis answers every Historical preset window. Each preset's frame
// (window, coverage, gross denominator, digests, scope) is the server's own for
// that window; this module re-bases only the window arithmetic from per-day sums.
export const HISTORY_BASIS_CONTRACT = 'portfolio-history-basis.v1';
export const BASIS_PRESETS = Object.freeze(['1W', 'MTD', '1M', '3M', 'YTD', '1Y']);
const COMPONENTS = ['total', 'factor', 'residual', 'trading', 'non_trading', 'realized', 'unrealized', 'adjustment'];
const SIDES = ['long', 'short', 'net'];
const CUSTOM_RANGE = /^(\d{4}-\d{2}-\d{2})\.\.(\d{4}-\d{2}-\d{2})$/;

const presetWindow = (basis, preset) => (preset?.kind === 'complete' ? preset.response?.window
    : preset?.kind === 'rebase' ? { ...basis.common, ...preset.frame }.window : null);

// The preset the basis can show for a lookback: one it names, or whose exact
// window a custom range equals. Null when the server must answer that window.
export function basisPresetKey(basis, lookback) {
    if (!basis?.presets || !lookback) return null;
    const range = CUSTOM_RANGE.exec(lookback);
    const key = BASIS_PRESETS.includes(lookback) ? lookback : range && BASIS_PRESETS.find((preset) => {
        const window = presetWindow(basis, basis.presets[preset]);
        return window?.start_date === range[1] && window?.end_date === range[2];
    });
    return key && presetWindow(basis, basis.presets[key]) ? key : null;
}

const contribution = (dollars, gross) => ({ contribution_pct: dollars / gross, contribution_dollar: dollars });
const total = (values) => values.reduce((sum, value) => sum + value, 0);

// The server's daily and cumulative shares: cumulative adds each day in order
// from the first window day, and dollars are each share times the gross.
function shareSeries(dates, daily, gross) {
    const series = { absolute_pct: {}, absolute_dollar: {}, cumulative_pct: {}, cumulative_dollar: {} };
    let cumulative = 0;
    daily.forEach((value, index) => {
        const date = dates[index];
        cumulative = index ? cumulative + value : value;
        series.absolute_pct[date] = value;
        series.absolute_dollar[date] = value * gross;
        series.cumulative_pct[date] = cumulative;
        series.cumulative_dollar[date] = cumulative * gross;
    });
    return series;
}

// One preset as a window response, or null when the server must answer it.
// Factor rows carry contributions only; per-factor exposures and per-holding
// detail stay with the inspector's direct request.
export function deriveBasisView(basis, key) {
    const preset = basis?.presets?.[key];
    if (!presetWindow(basis, preset)) return null;
    const context = basis.portfolio_context;
    const portfolioContext = { ...context, scope: { ...context.scope, cognive_ids: preset.scope_ids } };
    if (preset.kind === 'complete') return { ...preset.response, portfolio_context: portfolioContext, basis_digest: basis.basis_digest };
    const frame = { ...basis.common, ...preset.frame, portfolio_context: portfolioContext };
    const gross = frame.denominator.gross_notional;
    const series = basis.series[preset.series];
    const dates = series.dates.slice(preset.offset);
    const factorDollars = series.factor_dollars.slice(preset.offset);
    const returns = { dates, frequency: 'daily', by_factor: {} };
    const factorRows = basis.factors.map((factor, column) => {
        const daily = factorDollars.map((row) => row[column] / gross);
        returns.by_factor[factor] = shareSeries(dates, daily, gross);
        const share = total(daily);
        return { factor, family: basis.factor_families[column], contribution_pct: share, contribution_dollar: share * gross };
    });
    for (const name of COMPONENTS) returns[name === 'residual' ? 'idio' : name] = shareSeries(dates, preset.components[name].map((value) => value / gross), gross);
    const families = new Map();
    for (const row of factorRows) families.set(row.family, (families.get(row.family) ?? 0) + row.contribution_pct);
    return {
        ...frame,
        returns,
        contribution_summary: {
            components: Object.fromEntries(COMPONENTS.map((name) => [name, contribution(total(preset.components[name]), gross)])),
            by_factor: [...factorRows, ...basis.catalog_only_rows],
            by_family: [...families].map(([family, share]) => ({ family, contribution_pct: share, contribution_dollar: share * gross })),
            long_short: Object.fromEntries(SIDES.map((side) => [side, {
                components: Object.fromEntries(COMPONENTS.map((name) => [name, contribution(preset.long_short[side][name], gross)])),
            }])),
            groups: preset.groups.map((partition) => ({
                attribute_definition_id: partition.attribute_definition_id,
                groups: partition.groups.map(({ group, label, factor_dollar: dollars }) => ({ group, label, components: { factor: contribution(dollars, gross) } })),
            })),
        },
        summary_metrics: preset.summary_metrics,
        basis_digest: basis.basis_digest,
    };
}
